const en = {
  navigation: 'Organization',
  administration: 'Administration',
  title: 'Organization settings',
  description: 'Manage the countries, branches, departments, and teams that form your operating structure.',
  settings: 'Organization',
  settingsDescription: 'Maintain organization-wide identity and localization defaults.',
  masterData: 'Organization master data',
  loading: 'Loading organization settings…',
  loadingList: 'Loading records…',
  loadError: 'Organization settings could not be loaded.',
  emptyTitle: 'No records found',
  emptyDescription: 'Add a record or change the filters to continue.',
  add: 'Add record',
  addRecord: 'Add record',
  editRecord: 'Edit record',
  edit: 'Edit',
  activate: 'Activate',
  deactivate: 'Deactivate',
  active: 'Active',
  inactive: 'Inactive',
  destinationEnabled: 'Destination',
  destinationDisabled: 'Not a destination',
  noBranch: 'No branch',
  close: 'Close',
  cancel: 'Cancel',
  save: 'Save',
  saving: 'Saving…',
  saveSettings: 'Save settings',
  organizationSaved: 'Organization settings saved.',
  masterSaved: 'Record saved.',
  statusSaved: 'Status updated.',
  tabs: {
    countries: 'Countries',
    branches: 'Branches',
    departments: 'Departments',
    teams: 'Teams',
  },
  columns: {
    country: 'Country',
    branch: 'Branch',
    department: 'Department',
    team: 'Team',
    code: 'Code',
    currency: 'Currency',
    timezone: 'Timezone',
    destination: 'Destination',
    status: 'Status',
    actions: 'Actions',
  },
  fields: {
    name: 'Name',
    legalName: 'Legal name',
    code: 'Code',
    currency: 'Currency code',
    timezone: 'Timezone',
    destinationCountry: 'Use as a Query destination',
    country: 'Country',
    branch: 'Branch',
    department: 'Department',
    defaultCurrency: 'Default currency',
    defaultTimezone: 'Default timezone',
  },
  filters: {
    status: 'Status',
    country: 'Country',
    branch: 'Branch',
    department: 'Department',
    all: 'All',
  },
} as const;

const ja = {
  navigation: '組織',
  administration: '管理',
  title: '組織設定',
  description: '運用構造を構成する国、支店、部門、チームを管理します。',
  settings: '組織',
  settingsDescription: '組織全体の一貫した識別情報とローカライゼーションのデフォルト値を管理します。',
  masterData: '組織マスターデータ',
  loading: '組織設定を読み込み中…',
  loadingList: 'レコードを読み込み中…',
  loadError: '組織設定を読み込めませんでした。',
  emptyTitle: 'レコードが見つかりません',
  emptyDescription: 'レコードを追加するか、フィルターを変更してください。',
  add: 'レコードを追加',
  addRecord: 'レコードを追加',
  editRecord: 'レコードを編集',
  edit: '編集',
  activate: '有効化',
  deactivate: '無効化',
  active: '有効',
  inactive: '無効',
  destinationEnabled: '目的地',
  destinationDisabled: '目的地ではない',
  noBranch: '支店なし',
  close: '閉じる',
  cancel: 'キャンセル',
  save: '保存',
  saving: '保存中…',
  saveSettings: '設定を保存',
  organizationSaved: '組織設定を保存しました。',
  masterSaved: 'レコードを保存しました。',
  statusSaved: 'ステータスを更新しました。',
  tabs: {
    countries: '国',
    branches: '支店',
    departments: '部門',
    teams: 'チーム',
  },
  columns: {
    country: '国',
    branch: '支店',
    department: '部門',
    team: 'チーム',
    code: 'コード',
    currency: '通貨',
    timezone: 'タイムゾーン',
    destination: '目的地',
    status: 'ステータス',
    actions: '操作',
  },
  fields: {
    name: '名前',
    legalName: '正式名称',
    code: 'コード',
    currency: '通貨コード',
    timezone: 'タイムゾーン',
    destinationCountry: 'クエリの目的地として使用',
    country: '国',
    branch: '支店',
    department: '部門',
    defaultCurrency: 'デフォルト通貨',
    defaultTimezone: 'デフォルトタイムゾーン',
  },
  filters: {
    status: 'ステータス',
    country: '国',
    branch: '支店',
    department: '部門',
    all: 'すべて',
  },
} as const;

const zhTW = {
  navigation: '組織',
  administration: '管理',
  title: '組織設定',
  description: '管理構成營運架構的國家、分公司、部門與團隊。',
  settings: '組織',
  settingsDescription: '維護組織層級的識別資訊與預設在地化設定。',
  masterData: '組織主資料',
  loading: '正在載入組織設定…',
  loadingList: '正在載入記錄…',
  loadError: '無法載入組織設定。',
  emptyTitle: '找不到記錄',
  emptyDescription: '請新增記錄或調整篩選條件。',
  add: '新增記錄',
  addRecord: '新增記錄',
  editRecord: '編輯記錄',
  edit: '編輯',
  activate: '啟用',
  deactivate: '停用',
  active: '啟用',
  inactive: '停用',
  destinationEnabled: '目的地',
  destinationDisabled: '非目的地',
  noBranch: '無分公司',
  close: '關閉',
  cancel: '取消',
  save: '儲存',
  saving: '儲存中…',
  saveSettings: '儲存設定',
  organizationSaved: '組織設定已儲存。',
  masterSaved: '記錄已儲存。',
  statusSaved: '狀態已更新。',
  tabs: {
    countries: '國家',
    branches: '分公司',
    departments: '部門',
    teams: '團隊',
  },
  columns: {
    country: '國家',
    branch: '分公司',
    department: '部門',
    team: '團隊',
    code: '代碼',
    currency: '幣別',
    timezone: '時區',
    destination: '目的地',
    status: '狀態',
    actions: '操作',
  },
  fields: {
    name: '名稱',
    legalName: '法定名稱',
    code: '代碼',
    currency: '幣別代碼',
    timezone: '時區',
    destinationCountry: '作為查詢目的地使用',
    country: '國家',
    branch: '分公司',
    department: '部門',
    defaultCurrency: '預設幣別',
    defaultTimezone: '預設時區',
  },
  filters: {
    status: '狀態',
    country: '國家',
    branch: '分公司',
    department: '部門',
    all: '全部',
  },
} as const;

const ko = {
  navigation: '조직',
  administration: '관리',
  title: '조직 설정',
  description: '운영 구조를 구성하는 국가, 지점, 부서 및 팀을 관리합니다.',
  settings: '조직',
  settingsDescription: '조직 전반의 식별 정보와 기본 로컬라이제이션 설정을 유지 관리합니다.',
  masterData: '조직 마스터 데이터',
  loading: '조직 설정 불러오는 중…',
  loadingList: '기록 불러오는 중…',
  loadError: '조직 설정을 불러올 수 없습니다.',
  emptyTitle: '기록이 없습니다',
  emptyDescription: '기록을 추가하거나 필터를 변경하세요.',
  add: '기록 추가',
  addRecord: '기록 추가',
  editRecord: '기록 편집',
  edit: '편집',
  activate: '활성화',
  deactivate: '비활성화',
  active: '활성',
  inactive: '비활성',
  destinationEnabled: '목적지',
  destinationDisabled: '목적지가 아님',
  noBranch: '지점 없음',
  close: '닫기',
  cancel: '취소',
  save: '저장',
  saving: '저장 중…',
  saveSettings: '설정 저장',
  organizationSaved: '조직 설정을 저장했습니다.',
  masterSaved: '기록을 저장했습니다.',
  statusSaved: '상태를 업데이트했습니다.',
  tabs: {
    countries: '국가',
    branches: '지점',
    departments: '부서',
    teams: '팀',
  },
  columns: {
    country: '국가',
    branch: '지점',
    department: '부서',
    team: '팀',
    code: '코드',
    currency: '통화',
    timezone: '시간대',
    destination: '목적지',
    status: '상태',
    actions: '작업',
  },
  fields: {
    name: '이름',
    legalName: '법인명',
    code: '코드',
    currency: '통화 코드',
    timezone: '시간대',
    destinationCountry: '쿼리 목적지로 사용',
    country: '국가',
    branch: '지점',
    department: '부서',
    defaultCurrency: '기본 통화',
    defaultTimezone: '기본 시간대',
  },
  filters: {
    status: '상태',
    country: '국가',
    branch: '지점',
    department: '부서',
    all: '전체',
  },
} as const;

export const organizationMessages = {
  en,
  ja,
  'zh-TW': zhTW,
  ko,
} as const;
